import { Room } from 'livekit-client'
import SimplePeer from 'simple-peer'
import {
    P2pLocalAudioMedia,
    P2pLocalAudioManifest,
    P2pLocalVideoMedia,
    P2pRemoteAudio,
    P2pRemoteMedia,
    SfuRemoteAudio,
    SfuRemoteMedia,
    SfuLocalAudioManifest,
    P2pLocalVideoManifest,
    SfuLocalVideoManifest,
    LocalDesktopManifest,
    AudioEngineRemoteAudio,
} from '../RoomContext'
import { MuseMidiDeviceInfo } from './MusePeer'
import { ClientType } from '../UserContext'
import { v4 as uuidv4 } from 'uuid'

export type MusePeer = {
    username: string
    displayName?: string
    isGuest: boolean
    appSerialId: string
    sessionId: string
    internalUuid: string
    userUrn: string
    appVersion: string | null
    clientType: ClientType

    p2pConnection: SimplePeer.Instance | null
    sfuConnection: Room | null
    callInitiator: 'me' | 'them'
    connectionStatus:
        | 'initial'
        | 'connected' // 'connecting' |
        | 'closing'
        | 'disconnected_should_initiate_reconnect'
        | 'disconnected_standby'
        | 'closed'
    lastErrorCode: string | null
    lastDisconnectPreambleTime: Date | null

    remoteSfuVideo: SfuRemoteMedia | null
    remoteP2pVideo: P2pRemoteMedia | null
    remoteVideo: SfuRemoteMedia | P2pRemoteMedia | null
    remoteVideoControlStates: { isMuted: boolean } | null

    remoteSfuDesktop: SfuRemoteMedia | null
    remoteP2pDesktop: P2pRemoteMedia | null
    remoteDesktop: SfuRemoteMedia | P2pRemoteMedia | null

    remoteSfuAudios: SfuRemoteAudio[]
    remoteP2pAudios: P2pRemoteAudio[]
    remoteAudios: (SfuRemoteAudio | P2pRemoteAudio | AudioEngineRemoteAudio)[]
    remoteAudioContolStates: { [key: string]: { isMuted: boolean; isMutedBySolo: boolean; isSoloed: boolean } }
    remoteAudioRecordingState: { [key: string]: { isRecording: boolean | 'stopping' } }
    remoteMidiContolStates: { [key: string]: { isMuted: boolean } }

    localP2pAudios: P2pLocalAudioMedia[]
    localP2pVideo: P2pLocalVideoMedia | null
    localP2pDesktop: P2pLocalVideoMedia | null
    localP2pMidiDevices: MuseMidiDeviceInfo[]

    // this peer's manifest as seen by "me".
    manifest: PeerManifest
}

/**
 * MusePeer type constructor function
 */
export function MusePeer({
    userUrn,
    username,
    displayName,
    isGuest,
    sessionId,
    appSerialId,
    appVersion,
    clientType,
    callInitiator,
}: {
    userUrn: string
    username: string
    displayName: string
    isGuest: boolean
    sessionId: string
    appSerialId: string
    appVersion: string
    clientType: ClientType
    callInitiator: 'me' | 'them'
}): MusePeer {
    return {
        userUrn,
        username,
        displayName,
        isGuest,
        sessionId,
        appSerialId,
        internalUuid: uuidv4(),
        appVersion,
        clientType,

        callInitiator,

        p2pConnection: null,
        sfuConnection: null,
        connectionStatus: 'initial',
        lastErrorCode: null,

        // set in RoomController's disconnectPreambleHandler(). used to check
        // if the connection closing was an expected one.
        lastDisconnectPreambleTime: null,

        remoteP2pVideo: null,
        remoteSfuVideo: null,
        remoteVideo: null,
        remoteVideoControlStates: null,

        remoteP2pAudios: [],
        remoteSfuAudios: [],
        remoteAudios: [],
        remoteAudioContolStates: {},
        remoteAudioRecordingState: {},
        remoteMidiContolStates: {},

        remoteP2pDesktop: null,
        remoteSfuDesktop: null,
        remoteDesktop: null,

        localP2pAudios: [],
        localP2pVideo: null,
        localP2pDesktop: null,
        localP2pMidiDevices: [],

        // screenControlRequest: { reqId: 0, receiverSessionId: '', op: '' },
        // lastScreenControlRequestResponse: { reqId: 0, status: '' },

        manifest: PeerManifest(),
    }
}

export type PeerManifest = {
    localManifestV: number

    clientType: ClientType

    isScreenRecording: boolean
    isAudioDeprioritized: boolean

    // what devices the peer claims to have published to our p2p connection.
    localP2pAudios: P2pLocalAudioManifest[]
    localP2pVideo: P2pLocalVideoManifest | null
    localP2pDesktop: LocalDesktopManifest | null
    localP2pMidi: MuseMidiDeviceInfo[]

    // what devices the peer claims to have published to the
    // sfu (LiveKit) room.
    localSfuAudios: SfuLocalAudioManifest[]
    localSfuVideo: SfuLocalVideoManifest | null
    localSfuDesktop: LocalDesktopManifest | null
    localSfuMidi: MuseMidiDeviceInfo[]

    // this variable is updated when the remote peer described by this manifest sends a request
    // to this user wanting to control this user's screen.
    screenControlRequest: { reqId: number; receiverSessionId: string; op: 'start' | 'stop' | '' }
    // this variable is updated when the remote peer described by this manifest allows/denies/revokes
    // this user controlling the remote peer's screen.
    lastScreenControlRequestResponse: {
        reqId: number
        requesterSessionId?: string
        status: 'allowed' | 'denied' | 'revoked' | ''
    }

    galleryClientOrdering?: string[] | null // array of sessionIds
    videoHiddenSessionIds?: string[] | null // array of sessionIds
    applyHostGalleryPrefsToAllParticipants: boolean
}

export function PeerManifest(): PeerManifest {
    return {
        localManifestV: 0,

        clientType: '',

        isScreenRecording: false,
        isAudioDeprioritized: false,

        localP2pAudios: [],
        localP2pVideo: null,
        localP2pDesktop: null,
        localP2pMidi: [],

        localSfuAudios: [],
        localSfuVideo: null,
        localSfuDesktop: null,
        localSfuMidi: [],

        screenControlRequest: { reqId: 0, receiverSessionId: '', op: '' },
        lastScreenControlRequestResponse: { reqId: 0, status: '' },

        galleryClientOrdering: [],
        videoHiddenSessionIds: [],
        applyHostGalleryPrefsToAllParticipants: false,
    }
}

export type LiveKitMetadata = {
    sessionId: string
    userUrn: string
    manifest: PeerManifest
}
